// src/pages/Aide.jsx
import React, { useState } from 'react';
import { Row, Col, Card, Button, Accordion, Form, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';

const COLORS = {
  primary: '#295AA7',
  secondary: '#FDB52F',
  success: '#10b981',
  info: '#3b82f6'
};

const faqs = [
  {
    category: 'Général',
    questions: [
      {
        q: 'Comment créer un compte ?',
        a: 'Pour créer un compte, cliquez sur le bouton "S\'inscrire" sur la page de connexion. Remplissez le formulaire avec vos informations et validez.'
      },
      {
        q: 'J\'ai oublié mon mot de passe',
        a: 'Sur la page de connexion, cliquez sur "Mot de passe oublié". Entrez votre email et vous recevrez un lien pour réinitialiser votre mot de passe.'
      }
    ]
  },
  {
    category: 'Membres',
    questions: [
      {
        q: 'Comment ajouter un nouveau membre ?',
        a: 'Allez dans la section "Membres" puis cliquez sur "Ajouter un membre". Remplissez le formulaire avec les informations du nouveau membre.'
      },
      {
        q: 'Comment modifier les informations d\'un membre ?',
        a: 'Dans la liste des membres, cliquez sur le membre concerné puis sur le bouton "Modifier" en haut de sa fiche.'
      }
    ]
  },
  {
    category: 'Finances',
    questions: [
      {
        q: 'Comment enregistrer une offrande ?',
        a: 'Dans la section "Finances", choisissez "Entrées" puis "Nouvelle entrée". Sélectionnez le type et remplissez les informations.'
      },
      {
        q: 'Comment consulter le bilan financier ?',
        a: 'Allez dans "Finances" puis "Bilan". Vous y trouverez tous les rapports financiers avec des graphiques et tableaux.'
      }
    ]
  },
  {
    category: 'Sauvegarde',
    questions: [
      {
        q: 'Comment faire une sauvegarde ?',
        a: 'Dans "Paramètres", choisissez "Sauvegarde" puis "Sauvegarde manuelle". Sélectionnez les tables à sauvegarder et lancez le processus.'
      },
      {
        q: 'Comment restaurer une sauvegarde ?',
        a: 'Allez dans "Paramètres", "Sauvegarde" puis "Restauration". Choisissez le fichier de sauvegarde et confirmez la restauration.'
      }
    ]
  }
];

const guides = [
  {
    title: 'Guide de démarrage rapide',
    icon: 'ri-rocket-line',
    color: COLORS.primary,
    link: '/aide/guide-debutant'
  },
  {
    title: 'Gestion des membres',
    icon: 'ri-group-line',
    color: COLORS.success,
    link: '/aide/guide-membres'
  },
  {
    title: 'Gestion financière',
    icon: 'ri-money-euro-circle-line',
    color: COLORS.secondary,
    link: '/aide/guide-finances'
  },
  {
    title: 'Sauvegarde et restauration',
    icon: 'ri-database-2-line',
    color: COLORS.info,
    link: '/aide/guide-sauvegarde'
  }
];

export default function Aide() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeKey, setActiveKey] = useState(null);

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
           q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid">
          {/* Fil d'Ariane */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex align-items-center gap-2">
                <Button 
                  variant="link" 
                  className="text-decoration-none p-0"
                  onClick={() => navigate(-1)}
                  style={{ color: COLORS.primary }}
                >
                  <i className="ri-arrow-left-line fs-5"></i>
                </Button>
                <span className="text-muted">/</span>
                <span className="fw-semibold" style={{ color: COLORS.primary }}>Centre d'aide</span>
              </div>
            </Col>
          </Row>

          {/* En-tête */}
          <Row className="mb-5 text-center">
            <Col md={12}>
              <div className="mb-4">
                <div className="header-icon-wrapper d-inline-flex mb-3" style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i className="ri-question-line" style={{ fontSize: '40px', color: 'white' }}></i>
                </div>
                <h1 className="fw-bold mb-3" style={{ color: COLORS.primary }}>Comment pouvons-nous vous aider ?</h1>
                <p className="text-muted mb-4">Trouvez des réponses à vos questions et des guides détaillés</p>
              </div>

              {/* Barre de recherche */}
              <div className="search-container mx-auto" style={{ maxWidth: '600px' }}>
                <Form.Control
                  type="text"
                  size="lg"
                  placeholder="Rechercher dans l'aide..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    borderRadius: '50px',
                    padding: '1rem 1.5rem',
                    border: '2px solid #e2e8f0',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                  }}
                />
                <i className="ri-search-line" style={{ 
                  position: 'absolute',
                  right: '30px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: COLORS.primary,
                  fontSize: '1.2rem'
                }}></i>
              </div>
            </Col>
          </Row>

          {/* Guides rapides */}
          <Row className="mb-5">
            <Col md={12}>
              <h5 className="fw-semibold mb-4" style={{ color: COLORS.primary }}>
                <i className="ri-book-open-line me-2"></i>
                Guides de démarrage
              </h5>
            </Col>
            {guides.map((guide, index) => (
              <Col md={3} key={index}>
                <Card 
                  className="guide-card border-0 shadow-sm text-center h-100"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(guide.link)}
                >
                  <Card.Body className="p-4">
                    <div className="guide-icon mb-3" style={{ 
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '15px',
                      background: `${guide.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto'
                    }}>
                      <i className={guide.icon} style={{ color: guide.color, fontSize: '30px' }}></i>
                    </div>
                    <h6 className="fw-semibold mb-0">{guide.title}</h6>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* FAQ */}
          <Row>
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white border-0 pt-4">
                  <h5 className="fw-semibold mb-0" style={{ color: COLORS.primary }}>
                    <i className="ri-question-answer-line me-2"></i>
                    Foire aux questions
                  </h5>
                </Card.Header>
                <Card.Body>
                  {filteredFaqs.length > 0 ? (
                    <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)}>
                      {filteredFaqs.map((category, catIndex) => (
                        <div key={catIndex} className="mb-4">
                          <h6 className="fw-semibold mb-3" style={{ color: COLORS.primary }}>
                            {category.category}
                          </h6>
                          {category.questions.map((item, qIndex) => (
                            <Accordion.Item 
                              eventKey={`${catIndex}-${qIndex}`} 
                              key={qIndex}
                              className="mb-2 border-0"
                              style={{ background: '#f8fafc', borderRadius: '10px' }}
                            >
                              <Accordion.Header className="border-0">
                                <span className="fw-medium">{item.q}</span>
                              </Accordion.Header>
                              <Accordion.Body className="pt-0 text-muted">
                                {item.a}
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </div>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-file-search-line" style={{ fontSize: '3rem', color: COLORS.primary, opacity: 0.5 }}></i>
                      <p className="mt-3 text-muted">Aucun résultat trouvé pour "{searchTerm}"</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Contact support */}
          <Row className="mt-5">
            <Col md={12}>
              <Card className="border-0 shadow-sm text-center">
                <Card.Body className="p-5">
                  <i className="ri-customer-service-2-line" style={{ fontSize: '3rem', color: COLORS.primary }}></i>
                  <h5 className="fw-semibold mt-3 mb-2">Vous n'avez pas trouvé votre réponse ?</h5>
                  <p className="text-muted mb-4">Notre équipe de support est là pour vous aider</p>
                  <Button 
                    variant="primary"
                    style={{ background: COLORS.primary, border: 'none', borderRadius: '50px', padding: '0.8rem 2rem' }}
                    onClick={() => window.location.href = 'mailto:support@josephsbusiness.com'}
                  >
                    <i className="ri-mail-line me-2"></i>
                    Contacter le support
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        <style>{`
          .guide-card {
            transition: all 0.3s ease;
          }
          .guide-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(0,0,0,0.1) !important;
          }
          .search-container {
            position: relative;
          }
          .accordion-button:not(.collapsed) {
            background: ${COLORS.primary}10;
            color: ${COLORS.primary};
          }
          .accordion-button:focus {
            box-shadow: none;
            border-color: ${COLORS.primary}40;
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
}